import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ResendEmailPage from '../VerifyEmail/ResendEmailPage';

const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
	<div className="custom-datepicker-input custom_date" onClick={onClick} ref={ref}>
		<input type="text" value={value} readOnly placeholder="DD/MM/YYYY" className="form-control" />
		<img src="/assets/images/calendar.svg" className="cal_addicon" alt="calendar icon" />
	</div>
));

const BASE_URL =
	window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://dlqw5ngx5xpnk.cloudfront.net';

const Signup = () => {
	const [form, setForm] = useState({
		name: '',
		email: '',
		dateOfBirth: null,
		password: '',
		confirmPassword: '',
		terms: false,
	});
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [showLoader, setShowLoader] = useState(false);
	const [toastId, setToastId] = useState(null);
	const [showResendPage, setShowResendPage] = useState(false);
	const navigate = useNavigate();

	// Resend email state and handler (must be inside component)
	const [resending, setResending] = useState(false);
	const handleResendEmail = async () => {
		setResending(true);
		try {
			const res = await axios.post(
				`${BASE_URL}/api/auth/resend-verification`,
				{ email: form.email },
				{
					headers: { 'Content-Type': 'application/json' },
				}
			);
			if (res.data.success) {
				toast.success('Verification email resent to ' + form.email);
			} else {
				toast.error(res.data.message || 'Failed to resend email');
			}
		} catch (err) {
			toast.error(err.response?.data?.message || err.message || 'Failed to resend email');
		} finally {
			setResending(false);
		}
	};

	// Helper: calculate age from date
	const calculateAge = (dob) => {
		if (!dob) return 0;
		const today = new Date();
		const birthDate = new Date(dob);
		let age = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		return age;
	};

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
		setError('');
	};

	const handleDateChange = (date) => {
		setForm((prev) => ({ ...prev, dateOfBirth: date }));
		setError('');
	};

	// Password strength: min 8 chars, upper, lower, number, special
	const isStrongPassword = (pw) => {
		return pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw);
	};

	const handleSignup = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');
		setFieldErrors({});
		// Password match validation
		if (form.password !== form.confirmPassword) {
			setError('');
			setFieldErrors({ confirmPassword: 'Password and Confirm Password do not match.' });
			setLoading(false);
			return;
		}

		let errors = {};
		if (!form.name.trim()) errors.name = 'Full Name is required.';
		if (!form.email.trim()) errors.email = 'Email is required.';
		if (!form.dateOfBirth) errors.dateOfBirth = 'Date of Birth is required.';
		if (!form.password) errors.password = 'Password is required.';
		if (!form.confirmPassword) errors.confirmPassword = 'Confirm Password is required.';
		if (!form.terms) errors.terms = 'You must agree to the Terms and Conditions to continue.';

		// Age check
		const age = calculateAge(form.dateOfBirth);
		if (form.dateOfBirth && age < 18) errors.dateOfBirth = 'You must be at least 18 years old to apply.';
		// Email format
		if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
			errors.email = 'Please enter a valid email address.';
		// Password strength
		if (form.password && !isStrongPassword(form.password))
			errors.password =
				'Password must be at least 8 characters and include upper/lowercase, number, and special character.';
		// Confirm password
		// if (form.password && form.confirmPassword && form.password !== form.confirmPassword) errors.confirmPassword = "Passwords do not match.";

		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			setError('Please fix the errors below.');
			setShowLoader(false);
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			// Remove confirmPassword from payload
			const payload = { ...form };
			delete payload.confirmPassword;
			payload.dateOfBirth = form.dateOfBirth ? format(form.dateOfBirth, 'yyyy-MM-dd') : null;
			const res = await axios.post(`${BASE_URL}/api/auth/signup`, payload, {
				headers: { 'Content-Type': 'application/json' },
			});
			if (res.data.success) {
				// Instead of toast, show resend page
				setShowResendPage(true);
			} else {
				setError(res.data.message || 'Signup failed');
			}
		} catch (err) {
			setError(err.response?.data?.message || err.message || 'Signup failed');
		} finally {
			setLoading(false);
			setShowLoader(false);
		}
	};

	if (showResendPage) {
		return <ResendEmailPage email={form.email} />;
	}
	console.log('Loading ', showLoader, loading);
	return (
		<div className="container container_cust">
			<Row>
				<Col xl="12">
					<h2 className="personalinfo_head2">Fellowship Application</h2>
				</Col>
			</Row>

			<Row>
				<Col xl="4">
					<div className="formbg">
						<h3>Signup</h3>
						{/* <p>Please fill in all fields to create an account.</p> */}

						<form onSubmit={handleSignup}>
							<div className="form-floating mb-3 mt-3">
								<input
									type="text"
									className={`form-control form_custom${fieldErrors.name ? ' is-invalid' : ''}`}
									id="name"
									placeholder="Enter full name"
									name="name"
									value={form.name}
									onChange={handleChange}
									required
								/>
								<label htmlFor="name">
									Full Name <span className="text-danger">*</span>
								</label>
								{fieldErrors.name && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{fieldErrors.name}
									</div>
								)}
							</div>

							<div className="form-floating mb-3 mt-3">
								<input
									type="email"
									className={`form-control form_cap form_custom${fieldErrors.email ? ' is-invalid' : ''}`}
									id="email"
									placeholder="Enter email"
									name="email"
									value={form.email}
									onChange={handleChange}
									required
								/>
								<label htmlFor="email">
									Email <span className="text-danger">*</span>
								</label>
								{fieldErrors.email && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{fieldErrors.email}
									</div>
								)}
							</div>

							<div className="react_date_wrap react_date_wrap_pos">
								<h5>
									Date Of Birth <span>*</span>
								</h5>
								<DatePicker
									selected={form.dateOfBirth}
									onChange={handleDateChange}
									dateFormat="dd/MM/yyyy"
									showYearDropdown
									maxDate={new Date()}
									showMonthDropdown
									dropdownMode="select"
									customInput={
										<CustomInput
											value={form.dateOfBirth ? format(form.dateOfBirth, 'dd/MM/yyyy') : ''}
											onClick={(e) => e.stopPropagation()}
										/>
									}
								/>
								{fieldErrors.dateOfBirth && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{fieldErrors.dateOfBirth}
									</div>
								)}
							</div>

							<div className="form-floating mb-3 mt-3">
								<input
									type="password"
									className={`form-control form_cap form_custom${fieldErrors.password ? ' is-invalid' : ''}`}
									id="password"
									placeholder="Password"
									name="password"
									value={form.password}
									onChange={handleChange}
									required
								/>
								<label htmlFor="password">
									Password <span className="text-danger">*</span>
								</label>
								{fieldErrors.password && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{fieldErrors.password}
									</div>
								)}
							</div>

							<div className="form-floating mb-3 mt-3">
								<input
									type="password"
									className={`form-control form_cap form_custom${fieldErrors.confirmPassword ? ' is-invalid' : ''}`}
									id="confirmPassword"
									placeholder="Confirm Password"
									name="confirmPassword"
									value={form.confirmPassword}
									onChange={handleChange}
									required
								/>
								<label htmlFor="confirmPassword">
									Confirm Password <span className="text-danger">*</span>
								</label>
								{fieldErrors.confirmPassword && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{fieldErrors.confirmPassword}
									</div>
								)}
							</div>

							<div className="form-check mb-3 mt-3">
								<input
									className={`form-check-input${fieldErrors.terms ? ' is-invalid' : ''}`}
									type="checkbox"
									id="terms"
									name="terms"
									checked={form.terms}
									onChange={handleChange}
									required
								/>
								<label className="form-check-label" htmlFor="terms">
									I agree to the{' '}
									<a
										href="https://www.bluekraft.in/fellowship/term-condition/"
										target="_blank"
										rel="noopener noreferrer"
										style={{ textDecoration: 'none', color: 'rgba(0, 0, 0, 0.5)' }}>
										<u>Terms and Conditions</u>
									</a>{' '}
									and agree to receive Email and SMS. <span className="text-danger">*</span>
								</label>
								{fieldErrors.terms && (
									<div className="invalid-feedback" style={{ display: 'block' }}>
										{fieldErrors.terms}
									</div>
								)}
							</div>

							<button className="login_btn" type="submit" disabled={showLoader || loading}>
								{showLoader || loading ? (
									<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
								) : (
									'SIGN UP'
								)}
							</button>
							{error && <div style={{ color: 'red', marginTop: 10 }}>{error}</div>}
							<ToastContainer position="bottom-left" autoClose={3000} />
							<div style={{ color: '#343a40', marginTop: 10 }}>
								<p>
									Already have an account?{' '}
									<a href="/login" style={{ textDecoration: 'none', color: '#FF5C36' }}>
										Sign In
									</a>
								</p>
							</div>

							{/* {error && <div style={{ color: "red", marginTop: 10 }}>{error}</div>} */}
						</form>
					</div>
				</Col>

				<Col xl="8">
					<div className="login_content">
						<h4>Fellowship Application Timeline</h4>
						<h5>Deadline </h5>
						<p>
							Completed applications must be submitted by <span>December 10, 2025.</span>
						</p>
						<h5>Selection</h5>
						<p>
							Applicants will be notified of their acceptance status by <span>December 24, 2025.</span>
						</p>
						<p>
							Selected applicants must confirm their acceptance of the fellowship offer by{' '}
							<span>December 31, 2025.</span>
						</p>
						<h5>Commencement</h5>
						<p>
							The fellowship officially begins on <span>January 1, 2026.</span>
						</p>
						<p>
							We encourage meaningful voices to join us in contributing to and enriching the narrative surrounding
							India's journey forward!
						</p>
					</div>
				</Col>
			</Row>
		</div>
	);
};

export default Signup;
